// LocalBusiness Schema for Petrobell
export const getLocalBusinessSchema = (language: 'sv' | 'en') => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Petrobell AB",
  "description": language === 'sv' 
    ? "Specialister på marina drivmedel. Vi levererar alla sorters drivmedel i hela Sverige med bunkerfartyg och tankbilar."
    : "Marine fuel specialists. We deliver all types of fuels throughout Sweden with bunker vessels and tank trucks.",
  "url": "https://petrobell.se",
  "logo": "https://image.jimcdn.com/app/cms/image/transf/dimension=574x10000:format=png/path/s411a2b5b18fcb029/image/ibf520ed4bd6a92ca/version/1505397001/image.png",
  "image": "https://petrobell.se/images/hav-bg.jpg",
  "telephone": "+46-8-XXX XX XX",
  "email": "info@petrobell.se",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "SE",
    "addressRegion": "Stockholm"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "59.3293",
    "longitude": "18.0686"
  },
  "areaServed": {
    "@type": "Country",
    "name": "Sweden"
  },
  "priceRange": "$$",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "08:00",
    "closes": "17:00"
  },
  "inLanguage": language === 'sv' ? "sv-SE" : "en-US"
});

// Organization Schema
export const getOrganizationSchema = (language: 'sv' | 'en') => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Petrobell AB",
  "url": "https://petrobell.se",
  "logo": "https://image.jimcdn.com/app/cms/image/transf/dimension=574x10000:format=png/path/s411a2b5b18fcb029/image/ibf520ed4bd6a92ca/version/1505397001/image.png",
  "description": language === 'sv' 
    ? "Marina drivmedel och bunkerleveranser i hela Sverige"
    : "Marine fuels and bunker deliveries throughout Sweden",
  "foundingDate": "1954",
  "sameAs": [],
  "inLanguage": language === 'sv' ? "sv-SE" : "en-US"
});

// Product Schema for fuel products
export const createProductSchema = (name: string, description: string, language: 'sv' | 'en') => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": name,
  "description": description,
  "category": language === 'sv' ? "Drivmedel" : "Fuel",
  "brand": {
    "@type": "Brand",
    "name": "Petrobell"
  },
  "manufacturer": {
    "@type": "Organization",
    "name": "Petrobell AB"
  },
  "inLanguage": language === 'sv' ? "sv-SE" : "en-US"
});

// Products list for structured data
export const getProductsSchemaList = (language: 'sv' | 'en') => [
  createProductSchema(
    "HVO100", 
    language === 'sv' 
      ? "Förnybart dieselalternativ med upp till 90% lägre CO2-utsläpp" 
      : "Renewable diesel alternative with up to 90% lower CO2 emissions",
    language
  ),
  createProductSchema(
    language === 'sv' ? "Diesel MK1" : "Diesel MK1", 
    language === 'sv' 
      ? "Miljöklass 1 diesel för marina användning" 
      : "Environmental class 1 diesel for marine use",
    language
  ),
  createProductSchema(
    language === 'sv' ? "Bensin 95" : "Petrol 95", 
    language === 'sv' 
      ? "Blyfri bensin 95 oktan för fritidsbåtar" 
      : "Unleaded petrol 95 octane for recreational boats",
    language
  ),
  createProductSchema(
    language === 'sv' ? "Bensin 98" : "Petrol 98", 
    language === 'sv' 
      ? "Blyfri bensin 98 oktan för högprestandamotorer" 
      : "Unleaded petrol 98 octane for high-performance engines",
    language
  ),
  createProductSchema(
    language === 'sv' ? "Eldningsolja" : "Heating Oil", 
    language === 'sv' 
      ? "Eldningsolja för uppvärmning och industriellt bruk" 
      : "Heating oil for heating and industrial use",
    language
  )
];

// WebSite Schema for search functionality
export const getWebsiteSchema = (language: 'sv' | 'en') => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Petrobell",
  "url": "https://petrobell.se",
  "description": language === 'sv' 
    ? "Specialister på marina drivmedel i Sverige" 
    : "Marine fuel specialists in Sweden",
  "inLanguage": language === 'sv' ? "sv-SE" : "en-US"
});

// Service Schema for fuel delivery
export const getServiceSchema = (language: 'sv' | 'en') => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": language === 'sv' 
    ? "Bunkerleverans av marina drivmedel" 
    : "Bunker delivery of marine fuels",
  "description": language === 'sv' 
    ? "Leverans av drivmedel med bunkerfartyg och tankbilar till sjöstationer, fartyg och entreprenader i hela Sverige" 
    : "Fuel delivery with bunker vessels and tank trucks to marine stations, vessels and contractors throughout Sweden",
  "provider": {
    "@type": "LocalBusiness",
    "name": "Petrobell AB"
  },
  "areaServed": {
    "@type": "Country",
    "name": "Sweden"
  },
  "serviceType": "Fuel Delivery",
  "inLanguage": language === 'sv' ? "sv-SE" : "en-US"
});

// Vessel Fleet Schema
export const getVesselFleetSchema = (language: 'sv' | 'en') => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": language === 'sv' ? "Petrobells fartygflotta" : "Petrobell's vessel fleet",
  "description": language === 'sv' 
    ? "Bunkerfartyg för leverans av marina drivmedel i Sverige" 
    : "Bunker vessels for marine fuel delivery in Sweden",
  "numberOfItems": 5,
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "M/T Långbjörn", "description": language === 'sv' ? "Bunkerfartyg med 160 m³ kapacitet" : "Bunker vessel with 160 m³ capacity" },
    { "@type": "ListItem", "position": 2, "name": "M/T Bunkerbjörn", "description": language === 'sv' ? "Bunkerfartyg med 95 m³ kapacitet" : "Bunker vessel with 95 m³ capacity" },
    { "@type": "ListItem", "position": 3, "name": "M/T Sjöbjörn 9", "description": language === 'sv' ? "Bunkerfartyg med 110 m³ kapacitet" : "Bunker vessel with 110 m³ capacity" },
    { "@type": "ListItem", "position": 4, "name": "M/T Storbjörn", "description": language === 'sv' ? "Bunkerfartyg med 70 m³ kapacitet" : "Bunker vessel with 70 m³ capacity" },
    { "@type": "ListItem", "position": 5, "name": "M/T Lillbjörn", "description": language === 'sv' ? "Bunkerfartyg med 25 m³ kapacitet" : "Bunker vessel with 25 m³ capacity" }
  ],
  "inLanguage": language === 'sv' ? "sv-SE" : "en-US"
});

// Legacy exports for backwards compatibility (will use Swedish as default)
export const localBusinessSchema = getLocalBusinessSchema('sv');
export const organizationSchema = getOrganizationSchema('sv');
export const websiteSchema = getWebsiteSchema('sv');
export const serviceSchema = getServiceSchema('sv');
export const productsSchemaList = getProductsSchemaList('sv');
